import { useState, useEffect, useRef, useCallback } from "react";

interface GeocodedLocation {
  readonly latitude: number;
  readonly longitude: number;
}

interface UseGeocodeResult {
  readonly location: GeocodedLocation | null;
  readonly pendingLocation: GeocodedLocation | null;
  readonly loading: boolean;
  readonly notFound: boolean;
  readonly acceptPending: () => void;
  readonly dismissPending: () => void;
}

function normalizePostcode(postcode: string): string {
  return postcode.replace(/\s+/g, "").toUpperCase();
}

export function useGeocode(postcode: string, hasExistingLocation: boolean): UseGeocodeResult {
  const [location, setLocation] = useState<GeocodedLocation | null>(null);
  const [pendingLocation, setPendingLocation] = useState<GeocodedLocation | null>(null);
  const [loading, setLoading] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const lastPostcodeRef = useRef("");

  useEffect(() => {
    const normalized = normalizePostcode(postcode);
    if (normalized.length < 5 || normalized === lastPostcodeRef.current) {
      return;
    }

    lastPostcodeRef.current = normalized;
    setLoading(true);
    setNotFound(false);
    setPendingLocation(null);

    const controller = new AbortController();
    const timer = setTimeout(async () => {
      try {
        const response = await fetch(
          `/api/geocode?postcode=${encodeURIComponent(normalized)}`,
          { signal: controller.signal },
        );
        if (!response.ok) {
          setLocation(null);
          setNotFound(true);
          return;
        }
        const data = await response.json();
        if (data.result?.latitude && data.result?.longitude) {
          const newLoc = {
            latitude: data.result.latitude,
            longitude: data.result.longitude,
          };
          if (hasExistingLocation) {
            setPendingLocation(newLoc);
          } else {
            setLocation(newLoc);
          }
          setNotFound(false);
        } else {
          setLocation(null);
          setNotFound(true);
        }
      } catch {
        if (!controller.signal.aborted) {
          setLocation(null);
          setNotFound(true);
        }
      } finally {
        if (!controller.signal.aborted) {
          setLoading(false);
        }
      }
    }, 400);

    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [postcode, hasExistingLocation]);

  const acceptPending = useCallback(() => {
    if (pendingLocation) {
      setLocation(pendingLocation);
      setPendingLocation(null);
    }
  }, [pendingLocation]);

  const dismissPending = useCallback(() => {
    setPendingLocation(null);
  }, []);

  return { location, pendingLocation, loading, notFound, acceptPending, dismissPending };
}
